import React, { memo, useState, useCallback, useEffect, useMemo } from 'react';
import { Tooltip } from '@mui/material';
import TradeValidationCard from './TradeValidationCard';
import './ToolExecution.css';

// Tool name to display name mapping
const TOOL_DISPLAY_NAMES = {
    'get_ticker_data': 'Market Data',
    'get_stock_fundamentals': 'Stock Fundamentals',
    'get_stock_news': 'Stock News',
    'get_economics_calendar': 'Economic Calendar',
    'web_search_preview': 'Web Search',
    'extract_trade_spec': 'Trade Validation',
    'execute_python_code': 'Python Code'
};

// Tool name to icon mapping
const TOOL_ICONS = {
    'get_ticker_data': (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M3.5 18.49l6-6.01 4 4L22 6.92l-1.41-1.41-7.09 7.97-4-4L2 16.99z"/>
        </svg>
    ),
    'get_stock_fundamentals': (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M19 3H5c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zM9 17H7v-7h2v7zm4 0h-2V7h2v10zm4 0h-2v-4h2v4z"/>
        </svg>
    ),
    'get_stock_news': (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M19 5v14H5V5h14m1.1-2H3.9c-.5 0-.9.4-.9.9v16.2c0 .4.4.9.9.9h16.2c.4 0 .9-.5.9-.9V3.9c0-.5-.5-.9-.9-.9zM11 7h6v2h-6V7zm0 4h6v2h-6v-2zm0 4h6v2h-6v-2zM7 7h2v2H7V7zm0 4h2v2H7v-2zm0 4h2v2H7v-2z"/>
        </svg>
    ),
    'get_economics_calendar': (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M19 3h-1V1h-2v2H8V1H6v2H5c-1.11 0-1.99.9-1.99 2L3 19c0 1.1.89 2 2 2h14c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H5V8h14v11zM9 10H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2zm-8 4H7v2h2v-2zm4 0h-2v2h2v-2zm4 0h-2v2h2v-2z"/>
        </svg>
    ),
    'web_search_preview': (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/>
        </svg>
    ),
    'extract_trade_spec': (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M9 16.17L4.83 12l-1.42 1.41L9 19 21 7l-1.41-1.41z"/>
        </svg>
    ),
    'execute_python_code': (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M9.4 16.6L4.8 12l4.6-4.6L8 6l-6 6 6 6 1.4-1.4zm5.2 0l4.6-4.6-4.6-4.6L16 6l6 6-6 6-1.4-1.4z"/>
        </svg>
    )
};

/**
 * ToolExecution component for displaying tool/function call execution
 */
const ToolExecution = memo(({
    toolId,
    toolName,
    status, // 'pending' | 'running' | 'success' | 'error'
    arguments: toolArgs,
    result,
    durationMs
}) => {
    const [showDetails, setShowDetails] = useState(false);
    const [isCollapsed, setIsCollapsed] = useState(false);

    // Auto-collapse completed tools after a delay (ChatGPT-style)
    // Note: We keep them visible but subtle, not completely hidden
    useEffect(() => {
        if (status === 'success' && !isCollapsed) {
            const timer = setTimeout(() => {
                setIsCollapsed(true);
            }, 3000); // Collapse after 3 seconds
            return () => clearTimeout(timer);
        } else if (status === 'running' || status === 'pending') {
            setIsCollapsed(false); // Expand when running
        }
    }, [status, isCollapsed]);

    const toggleDetails = useCallback(() => {
        setShowDetails(prev => !prev);
    }, []);

    const toggleCollapse = useCallback(() => {
        setIsCollapsed(prev => !prev);
    }, []);

    const displayName = TOOL_DISPLAY_NAMES[toolName] || toolName;
    const icon = TOOL_ICONS[toolName] || (
        <svg viewBox="0 0 24 24" width="16" height="16" fill="currentColor">
            <path d="M22.7 19l-9.1-9.1c.9-2.3.4-5-1.5-6.9-2-2-5-2.4-7.4-1.3L9 6 6 9 1.6 4.7C.4 7.1.9 10.1 2.9 12.1c1.9 1.9 4.6 2.4 6.9 1.5l9.1 9.1c.4.4 1 .4 1.4 0l2.3-2.3c.5-.4.5-1.1.1-1.4z"/>
        </svg>
    );

    const isRunning = status === 'running' || status === 'pending';
    const isSuccess = status === 'success';

    // Parse arguments for display
    let parsedArgs = null;
    if (toolArgs) {
        try {
            parsedArgs = typeof toolArgs === 'string' ? JSON.parse(toolArgs) : toolArgs;
        } catch (e) {
            parsedArgs = toolArgs;
        }
    }

    // For extract_trade_spec, parse the result into trade data
    const tradeData = useMemo(() => {
        if (toolName !== 'extract_trade_spec') return null;
        try {
            // Try to parse from result first
            if (result) {
                const parsed = typeof result === 'string' ? JSON.parse(result) : result;
                if (parsed.saved) {
                    return {
                        instrument: parsed.instrument,
                        direction: parsed.direction,
                        entryPrice: parsed.entry_price,
                        stopPrice: parsed.stop_price,
                        targetPrice: parsed.target_price
                    };
                }
            }
            // Fall back to parsed arguments
            if (parsedArgs) {
                return {
                    instrument: parsedArgs.instrument,
                    direction: parsedArgs.direction,
                    entryPrice: parsedArgs.entry_price,
                    stopPrice: parsedArgs.stop_price,
                    targetPrice: parsedArgs.target_price
                };
            }
            return null;
        } catch (e) {
            // Fall back to parsed arguments
            if (parsedArgs) {
                return {
                    instrument: parsedArgs.instrument,
                    direction: parsedArgs.direction,
                    entryPrice: parsedArgs.entry_price,
                    stopPrice: parsedArgs.stop_price,
                    targetPrice: parsedArgs.target_price
                };
            }
            return null;
        }
    }, [toolName, result, parsedArgs]);

    const formatDuration = (ms) => {
        if (!ms) return '';
        if (ms < 1000) return `${ms}ms`;
        return `${(ms / 1000).toFixed(1)}s`;
    };

    // Special rendering for extract_trade_spec - show trade card
    if (toolName === 'extract_trade_spec' && isSuccess && tradeData) {
        return (
            <div className={`ntb-tool-block ${status} trade-validation`}>
                <div className="ntb-tool-header" onClick={toggleDetails}>
                    <div className={`ntb-tool-icon ${status}`}>{icon}</div>
                    <span className="ntb-tool-name">{displayName}</span>
                    {durationMs && (
                        <span className="ntb-tool-duration">{formatDuration(durationMs)}</span>
                    )}
                </div>
                <TradeValidationCard trade={tradeData} />
            </div>
        );
    }

    return (
        <div className={`ntb-tool-block ${status} ${isCollapsed ? 'collapsed' : ''}`}>
            <div className="ntb-tool-header" onClick={() => {
                if (isCollapsed) {
                    toggleCollapse(); // Expand if collapsed
                } else if (isSuccess) {
                    toggleDetails(); // Toggle details if expanded and completed
                }
            }}>
                <div className={`ntb-tool-icon ${status}`}>
                    {isRunning ? (
                        <svg className="ntb-tool-spinner" viewBox="0 0 24 24" width="14" height="14">
                            <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="2" fill="none" strokeDasharray="31.4" strokeDashoffset="10" />
                        </svg>
                    ) : (
                        icon
                    )}
                </div>
                <span className="ntb-tool-name">{displayName}</span>
                {!isCollapsed && (
                    <>
                        {isRunning && (
                            <span className="ntb-tool-status-text">Running...</span>
                        )}
                        {durationMs && !isRunning && (
                            <span className="ntb-tool-duration">{formatDuration(durationMs)}</span>
                        )}
                    </>
                )}
                {isSuccess && !isRunning && !isCollapsed && (
                    <Tooltip title={showDetails ? "Hide details" : "Show details"} arrow placement="top">
                        <button
                            className="ntb-tool-toggle"
                            onClick={(e) => {
                                e.stopPropagation();
                                toggleDetails();
                            }}
                            aria-label={showDetails ? "Hide details" : "Show details"}
                        >
                            <svg
                                viewBox="0 0 24 24"
                                width="14"
                                height="14"
                                fill="currentColor"
                                style={{ transform: showDetails ? 'rotate(180deg)' : 'rotate(0deg)', transition: 'transform 0.2s' }}
                            >
                                <path d="M7.41 8.59L12 13.17l4.59-4.58L18 10l-6 6-6-6 1.41-1.41z"/>
                            </svg>
                        </button>
                    </Tooltip>
                )}
            </div>
            {showDetails && !isCollapsed && (
                <div className="ntb-tool-details">
                    {parsedArgs && (
                        <div className="ntb-tool-section">
                            <div className="ntb-tool-section-label">Input</div>
                            <div className="ntb-tool-section-content">
                                {typeof parsedArgs === 'object' ? (
                                    <pre>{JSON.stringify(parsedArgs, null, 2)}</pre>
                                ) : (
                                    <span>{String(parsedArgs)}</span>
                                )}
                            </div>
                        </div>
                    )}
                    {result && (
                        <div className="ntb-tool-section">
                            <div className="ntb-tool-section-label">Output</div>
                            <div className="ntb-tool-section-content">
                                <pre>{result}</pre>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
});

ToolExecution.displayName = 'ToolExecution';

export default ToolExecution;
